import type { BrActivationChannel, BrActivationState, ImportedIdentity } from '../../types/brActivation'
import { environmentVariables } from '../../utils/environmentVariables'
import {
  BrActivationSessionExpiredError,
  InvalidCodeError,
  InvalidPhoneError,
  InvalidPinError,
  PasswordPolicyError,
  PinUnavailableError,
} from '../../utils/errors'

/**
 * BR activation transport seam (contract pack §3/§4/§6/§7).
 *
 * This module is deliberately independent from `services/api/auth.ts`'s
 * `directoryApiClient`: the activation funnel is **JWT-gated**. Each backend
 * step returns a short-lived stage token (`token` at the top level of the
 * response envelope) which is persisted in a JS-readable `swile:activation`
 * cookie and forwarded as `Authorization: Bearer <token>` on every subsequent
 * request. No `credentials: 'include'`, no CSRF double-submit.
 *
 * Topology (contract pack §6, FROZEN 2026-06-10): endpoints span two base
 * paths — the **neutral engine** `/signin/activation/*` (reusable by any
 * country) and the thin **BR adapter** `/signin/activation/br/*` (the only
 * country-specific hinges: `pin`, `finalize`, `state`). A single BASE_PATH no
 * longer fits, so each call passes its full path below.
 */

/** Neutral activation engine — country-agnostic steps. */
const ENGINE_PATH = '/signin/activation'
/** BR country-adapter — only the BR-specific hinges live here. */
const BR_ADAPTER_PATH = '/signin/activation/br'

const apiUrl = (path: string): string => `${environmentVariables('AUTH_API_URL')}${path}`

// ---------------------------------------------------------------------------
// Funnel-token cookie helpers
// These are scoped to the activation funnel only — NOT reused for the real
// session token (`saveTokens` handles that separately after finalize).
// ---------------------------------------------------------------------------

const FUNNEL_COOKIE = 'swile:activation'

// Top-level to avoid per-call regex recompilation (lint/performance/useTopLevelRegex).
const FUNNEL_COOKIE_REGEX = /(?:^|;\s*)swile:activation=([^;]+)/

// Omit Secure on plain HTTP (jsdom / local dev) — browsers silently drop cookie
// writes with Secure on non-HTTPS pages, which would break the funnel in tests.
const SECURE_FLAG = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : ''

const getFunnelToken = (): string | null => {
  const match = document.cookie.match(FUNNEL_COOKIE_REGEX)
  return match ? decodeURIComponent(match[1]) : null
}

const setFunnelToken = (token: string): void => {
  // Short-lived, JS-readable, SameSite=Lax; Secure (HTTPS only). Not HttpOnly.
  // Expires in 35 minutes (slightly above the longest stage TTL of 30m).
  const expires = new Date(Date.now() + 35 * 60 * 1000).toUTCString()
  document.cookie = `${FUNNEL_COOKIE}=${encodeURIComponent(token)}; path=/; expires=${expires}; SameSite=Lax${SECURE_FLAG}`
}

export const clearFunnelToken = (): void => {
  document.cookie = `${FUNNEL_COOKIE}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax${SECURE_FLAG}`
}

type Envelope = {
  next: BrActivationState['step']
  data?: BrActivationState['data']
  token?: string // top-level stage token (absent for non-advancing responses like mfa/init)
}

type ErrorEnvelope = {
  error?: string
  details?: Record<string, unknown>
}

/**
 * Maps a non-OK response to a typed error. `/start` stays generic (anti-enumeration);
 * `/pin` exposes only `invalid_credentials` (wrong PIN) and `pin_unavailable` (every
 * other failure, no reason) — both generic. `invalid_code` / `password_policy` are the
 * other specific codes (contract §6/§7). 401 → session expired (orchestrator → /signin).
 */
const throwForStatus = async (response: Response): Promise<never> => {
  if (response.status === 401) {
    throw new BrActivationSessionExpiredError()
  }

  let body: ErrorEnvelope = {}
  try {
    body = (await response.json()) as ErrorEnvelope
  } catch {
    // ignore — fall through to generic error below
  }

  if (response.status === 422) {
    switch (body.error) {
      case 'invalid_credentials':
        throw new InvalidPinError()
      case 'pin_unavailable':
        throw new PinUnavailableError()
      case 'invalid_code':
        throw new InvalidCodeError()
      case 'invalid_phone':
        throw new InvalidPhoneError()
      case 'password_policy':
        throw new PasswordPolicyError(body.details)
    }
  }

  throw new Error(`BR activation request failed (${response.status})`, { cause: body })
}

const parseEnvelope = (body: Envelope): BrActivationState => ({
  step: body.next,
  data: body.data ?? {},
})

const post = (path: string, payload?: Record<string, unknown>, signal?: AbortSignal): Promise<Response> => {
  const token = getFunnelToken()
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }
  return fetch(apiUrl(path), {
    method: 'POST',
    headers,
    body: payload ? JSON.stringify(payload) : undefined,
    signal,
  })
}

const postEnvelope = async (
  path: string,
  payload?: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<BrActivationState> => {
  const response = await post(path, payload, signal)
  if (!response.ok) {
    await throwForStatus(response)
  }
  const body = (await response.json()) as Envelope
  if (body.token) {
    setFunnelToken(body.token)
  }
  return parseEnvelope(body)
}

/**
 * Resume the funnel. `GET /state` returns `{ step, data }` (NOT `{ next }`),
 * normalised here into the same shape as the POST responses. 401 → expired.
 */
export const fetchState = async (): Promise<BrActivationState> => {
  const token = getFunnelToken()
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }
  const response = await fetch(apiUrl(`${BR_ADAPTER_PATH}/state`), {
    method: 'GET',
    headers,
  })
  if (response.status === 401) {
    throw new BrActivationSessionExpiredError()
  }
  if (!response.ok) {
    await throwForStatus(response)
  }
  const body = (await response.json()) as { step: BrActivationState['step']; data?: BrActivationState['data'] }
  return { step: body.step, data: body.data ?? {} }
}

/**
 * Step 1 — CPF or email lookup. Generic by design (anti-enumeration).
 * NEUTRAL global front door (`/signin/activation/start`) — not under `/br`.
 */
export const start = async (identifier: string, signal?: AbortSignal): Promise<BrActivationState> =>
  postEnvelope(`${ENGINE_PATH}/start`, { identifier }, signal)

/**
 * Step 2 — PIN. Wrong PIN → InvalidPinError; every other failure → PinUnavailableError
 * (generic, no reason leaked). Success → `otp`. BR adapter route (`/signin/activation/br/pin`).
 */
export const submitPin = async (pin: string): Promise<BrActivationState> =>
  postEnvelope(`${BR_ADAPTER_PATH}/pin`, { pin })

/** Step 3 — request an MFA security code on a channel. Returns `{ ok: true }`, no step change. */
export const sendSecurityCode = async (channel: BrActivationChannel): Promise<void> => {
  const response = await post(`${ENGINE_PATH}/mfa/init`, { channel })
  if (!response.ok) {
    await throwForStatus(response)
  }
}

/** Step 3 — verify the MFA security code. Bad code → InvalidCodeError. */
export const verifySecurityCode = async (channel: BrActivationChannel, code: string): Promise<BrActivationState> =>
  postEnvelope(`${ENGINE_PATH}/mfa/check`, { channel, code })

/** Step 4 — submit an email (only when missing or @nomail.themenu.com.br). Phase-1 `…/email/init` (harmonized §6). */
export const initEmail = async (email: string): Promise<BrActivationState> =>
  postEnvelope(`${ENGINE_PATH}/email/init`, { email })

/** Step 4 — verify the email OTP. Bad code → InvalidCodeError. */
export const verifyEmail = async (code: string, email: string): Promise<BrActivationState> =>
  postEnvelope(`${ENGINE_PATH}/email/check`, { code, email })

/** Step 5 — submit a phone number (only when missing). Phase-1 `…/phone/init` (harmonized §6). */
export const submitPhone = async (phone: string): Promise<BrActivationState> =>
  postEnvelope(`${ENGINE_PATH}/phone/init`, { phone })

/** Step 5 — verify the phone SMS OTP. Bad code → InvalidCodeError. */
export const verifyPhone = async (code: string, phone: string): Promise<BrActivationState> =>
  postEnvelope(`${ENGINE_PATH}/phone/check`, { code, phone })

/** Step 6 — names info. `title` is optional (contract §6). */
export const submitNames = async (personal: {
  given_name: string
  family_name: string
  title?: string
}): Promise<BrActivationState> => postEnvelope(`${ENGINE_PATH}/names`, personal)

/** Step 7 — set the activation password (last write before finalize). Policy fail → PasswordPolicyError. */
export const submitPassword = async (password: string, passwordConfirmation: string): Promise<BrActivationState> =>
  postEnvelope(`${ENGINE_PATH}/password`, { password, password_confirmation: passwordConfirmation })

/**
 * Finalize — mints OAuth/Doorkeeper tokens and clears the funnel. Returns
 * `{ next:"success", data:{ account_uuid, token } }`; `data.token` carries the auth
 * tokens (contract §13) the orchestrator saves via `saveTokens` so the activated
 * user is authenticated like a normal login. The post-activation destination is
 * FE-owned (contract §12, no `redirect_url`). BR adapter route
 * (`/signin/activation/br/finalize`).
 *
 * NOTE: `data.token` is the real session auth token — distinct from the top-level
 * `token` field (stage JWT) used throughout the funnel. `parseEnvelope` correctly
 * puts it in `data.token`; the caller (`ActivationFlow`) handles it via `saveTokens`.
 */
export const finalize = async (): Promise<BrActivationState> => postEnvelope(`${BR_ADAPTER_PATH}/finalize`)

export type { ImportedIdentity }
